"use client";

import type { SupSingleVaultV3, SupWeb3VaultBalance } from "@/types";
import React, { useCallback, useMemo } from "react";

import useGetAdminConfig from "@/hooks/api/useGetAdminConfig";
import useDepositButton from "@/hooks/tx-flow/buttons/useDepositButton";
import useApeIn from "@/hooks/tx-flow/buttons/useApeIn";
import useWithdrawalButton from "@/hooks/tx-flow/buttons/useWithdrawalButton";

import { SupDepositWithdrawButton, SupLoader } from "@repo/core-ui";
import { useStoredDataDispatch } from "@/store/Context";
import { notifyWithdrawChange } from "@/utils";

import UiTooltipUnsupported from "@/components/Ui/Tooltip/Unsupported";

interface AdapterCartButtonProps {
  singleVault: SupSingleVaultV3;
  isFluid?: boolean;
  withdrawFirst?: boolean;
  noWithdraw?: boolean;
  vaultBalance: SupWeb3VaultBalance;
}

export default function AdapterCartButton({ singleVault, isFluid = false, withdrawFirst = true, vaultBalance }: AdapterCartButtonProps) {
  const storedDispatch = useStoredDataDispatch();

  const { data, isLoading: isLoadingConfig } = useGetAdminConfig();

  const isDisabledAll = useMemo(() => (data ? data.isDepositDisabled && data.isWithdrawalDisabled : false), [data]);

  const { isDisabled: isDisabledWithdrawal, isCompleted: isCompletedWithdrawal, reason: reasonWithdrawal } = useWithdrawalButton(singleVault, vaultBalance, data);
  const { isDisabled, isCompleted, reason, isLoading: isLoadingDeposit } = useDepositButton(singleVault);
  const handleApeIn = useApeIn(singleVault);

  const isLoading = useMemo(() => isLoadingDeposit || isLoadingConfig, [isLoadingConfig, isLoadingDeposit]);

  const onClickDeposit = useCallback(() => {
    handleApeIn();
  }, [handleApeIn]);

  const onClickDepositRemove = useCallback(() => {
    storedDispatch({ vault: singleVault, type: "toggleCart" });
  }, [singleVault, storedDispatch]);

  const onClickWithdraw = useCallback(() => {
    storedDispatch({ vault: singleVault, type: "toggleWithdrawCart", balance: vaultBalance });
    notifyWithdrawChange(singleVault, vaultBalance, storedDispatch);
  }, [singleVault, storedDispatch, vaultBalance]);

  const onClickWithdrawRemove = useCallback(() => {
    storedDispatch({ vault: singleVault, type: "toggleWithdrawCart", balance: vaultBalance });
  }, [singleVault, storedDispatch, vaultBalance]);

  return isLoading ? (
    <SupLoader isSmall />
  ) : (
    <UiTooltipUnsupported
      arrowPlacement="right"
      enable={isDisabledWithdrawal || isDisabled}
      reason={isDisabledAll ? "disabled" : isDisabledWithdrawal ? reasonWithdrawal : isDisabled ? reason : undefined}
    >
      <SupDepositWithdrawButton
        completed={[isCompleted, isCompletedWithdrawal]}
        defaultButton={withdrawFirst ? "secondary" : "primary"}
        onDeposit={onClickDeposit}
        onWithdraw={onClickWithdraw}
        onRemoveDeposit={onClickDepositRemove}
        onRemoveWithdraw={onClickWithdrawRemove}
        disabled={[isDisabled, isDisabledWithdrawal]}
        fluid={isFluid}
      />
    </UiTooltipUnsupported>
  );
}
